import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Box, Toolbar } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import GlobalLoader from './components/GlobalLoader';
import SideNav from './components/SideNav';
import TopNav from './components/TopNav';
import OfflineNotificationBar from './components/OfflineNotificationBar';
import PrivateRoute from './components/PrivateRoute';
import ModuleProtectedRoute, { UnauthorizedPage } from './components/ModuleProtectedRoute';
import './styles/App.css';
import DashboardPage from './pages/DashboardPage';

// ✅ Updated Customer imports
import CustomersPage from './features/customers/pages/CustomersPage';
import CustomerDetailPage from './features/customers/pages/CustomerDetailPage';
import AddCustomerPage from './features/customers/pages/AddCustomerPage';

import ProductsListPage from './features/product/pages/ProductsListPage';  
import ProposalsPage from './pages/ProposalsPage';
import CreditNotesPage from './pages/CreditNotesPage';
import PaymentsPage from './pages/PaymentsPage';
import PurchaseOrdersListPage from './features/purchases/pages/PurchaseOrdersListPage';
import ReportsPage from './features/reports/pages/ReportsPage';
import CashMovementLedgerPage from './features/cashledger/pages/CashMovementLedgerPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import AnnouncementPage from './features/announcements/pages/AnnouncementPage';

import 'react-toastify/dist/ReactToastify.css';
import ListInvoices from './features/invoices/pages/ListInvoices';
import AddInvoicePage from './features/invoices/pages/AddInvoicePage';
import InvoiceViewPage from './features/invoices/pages/InvoiceViewPage';
import ReceivedPeppolInvoices from './features/invoices/pages/ReceivedPeppolInvoices';
import PeppolInvoiceView from './features/invoices/pages/PeppolInvoiceView';
import ListDeliveryNotes from './features/deliverynotes/pages/ListDeliveryNotes';
import AddDeliveryNotePage from './features/deliverynotes/pages/AddDeliveryNotePage';
import DeliveryNoteViewPage from './features/deliverynotes/pages/DeliveryNoteViewPage';
import ProductDetailPage from './features/product/pages/ProductDetailPage';
import AddProductPage from './features/product/pages/AddProductPage';
import CompanySettingsPage from './features/companysettings/pages/CompanySettingsPage';
import ProfileSettingsPage from './features/companysettings/pages/ProfileSettingsPage';
import AddStoreAndUser from './features/companysettings/components/AddStoreAndUser';
import ListSuppliers from './features/suppliers/pages/ListSuppliers';
import AddSupplier from './features/suppliers/pages/AddSupplier';
import EditSupplier from './features/suppliers/pages/EditSupplier';
import SupplierDetailPage from './pages/SupplierDetailPage';
import AddCreditNotePage from './features/creditnotes/pages/AddCreditNotePage';
import ListCreditNotesPage from './features/creditnotes/pages/ListCreditNotesPage';
import CreditNoteDetailPage from './features/creditnotes/pages/CreditNoteDetailPage';

import AddOfferPage from './features/offers/pages/AddOfferPage';
import ListOffersPage from './features/offers/pages/ListOffersPage';
import OfferDetailPage from './features/offers/pages/OfferDetailPage';
import AddSaleOrderPage from './features/saleorder/pages/AddSaleOrderPage';
import ListSaleOrderPage from './features/saleorder/pages/ListSaleOrderPage';
import SaleOrderDetailPage from './features/saleorder/pages/SaleOrderDetailPage';
import ConvertToInvoicePage from './features/saleorder/pages/ConvertToInvoicePage';
import "./locales/i18n";
import WishlistPage from './features/wishlist/pages/WishlistPage';
import NotFoundPage from './pages/NotFoundPage';

import AddPurchaseOrderPage from "./features/purchases/pages/AddPurchaseOrderPage";
import PurchaseOrderDetailPage from "./features/purchases/pages/PurchaseOrderDetailPage";
import EditPurchaseOrderPage from "./features/purchases/pages/EditPurchaseOrderPage";
import ComingSoon from './pages/ComingSoon';
import ShopifyProductsListPage from './features/shopify/pages/ShopifyProductsListPage';
import AddShopifyProduct from './features/shopify/pages/AddShopifyProduct';
import ShopifyProductDetailPage from './features/shopify/pages/ShopifyProductDetailPage';
import EditShopifyProductPage from './features/shopify/pages/EditShopifyProductPage';
import ShopifyCustomersPage from './features/shopify/pages/ShopifyCustomersPage';
import ShopifyOrdersPage from './features/shopify/pages/ShopifyOrdersPage';
import PosListPage from './features/pos/pages/PosListPage';
import AuthInitializer from './components/AuthInitializer';
import PaymentPage from './features/payments/pages/PaymentPage';
import TicketDeCaissePage from './features/ticketdecaisse/pages/TicketDeCaissePage';
import TicketDetailPage from './features/ticketdecaisse/pages/TicketDetailPage';
import TicketEditPage from './features/ticketdecaisse/pages/TicketEditPage';
import InventoryPage from './features/inventory/pages/InventoryPage';
import HomePage from './features/home/pages/HomePage';
import PrivacyPolicyPage from './features/home/pages/PrivacyPolicyPage';
import TermsOfServicePage from './features/home/pages/TermsOfServicePage';
import ProductInventoryDetail from './features/inventory/pages/ProductInventoryDetail';
import AccessManagementPage from './features/access-management/pages/AccessManagementPage';
import AdminUserManagementPage from './features/adminusermanagement/pages/AdminUserManagementPage';
import CompanyEditPage from './features/adminusermanagement/pages/CompanyEditPage';
import ProductVariantsPage from './features/product/pages/ProductVariantsPage';
import InventoryItemsPage from './features/inventory/pages/InventoryItemsPage';
import TransferListPage from './features/inventory/pages/TransferListPage';
import CreateTransferPage from './features/inventory/pages/CreateTransferPage';
import { AuthProvider } from './contexts/AuthContext';

// Accountant Portal imports
import AccountantDashboard from './features/accountant-portal/pages/AccountantDashboard';
import ClientsListPage from './features/accountant-portal/pages/ClientsListPage';
import ClientDetailPage from './features/accountant-portal/pages/ClientDetailPage';
import CreateClientPage from './features/accountant-portal/pages/CreateClientPage';
import EditClientPage from './features/accountant-portal/pages/EditClientPage';
import IncomingInvoicesPage from './features/accountant-portal/pages/IncomingInvoicesPage';
import OutgoingInvoicesPage from './features/accountant-portal/pages/OutgoingInvoicesPage';
import CreditNotesPageAP from './features/accountant-portal/pages/CreditNotesPage';
import BankingPage from './features/accountant-portal/pages/BankingPage';
import DocumentsPage from './features/accountant-portal/pages/DocumentsPage';
import VATTaxesPage from './features/accountant-portal/pages/VATTaxesPage';
import ReportsPageAP from './features/accountant-portal/pages/ReportsPage';
import TasksPage from './features/accountant-portal/pages/TasksPage';
import CreateTaskPage from './features/accountant-portal/pages/CreateTaskPage';
import MessagesPage from './features/accountant-portal/pages/MessagesPage';
import CalendarPage from './features/accountant-portal/pages/CalendarPage';
import SettingsPageAP from './features/accountant-portal/pages/SettingsPage';

const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notificationVisible, setNotificationVisible] = useState(false);
  
  const isAuthRoute = ['/', '/home', '/login', '/register', '/forgot-password', '/privacy-policy', '/terms-of-service'].includes(location.pathname);
  const isPosRoute = location.pathname === '/pos';
  const isAccountantRoute = location.pathname.startsWith('/accountant-portal');

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  useEffect(() => {
    const handleNotificationVisibility = (event) => {
      setNotificationVisible(event.detail.visible);
    };

    window.addEventListener('offlineNotificationVisibility', handleNotificationVisibility);
    return () => {
      window.removeEventListener('offlineNotificationVisibility', handleNotificationVisibility);
    };
  }, []);

  return (
    <>
      {isPosRoute ? (
        <Routes>
          <Route
            path="/pos"
            element={
              <PrivateRoute>
                <ModuleProtectedRoute moduleName="POS" requiredPermission="view">
                  <PosListPage />
                </ModuleProtectedRoute>
              </PrivateRoute>
            }
          />
        </Routes>
      ) : !isAuthRoute ? (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
          {!isAccountantRoute && <TopNav onMenuClick={handleDrawerToggle} />}
          {!isAccountantRoute && <OfflineNotificationBar />}
          {!isAccountantRoute && <SideNav mobileOpen={mobileOpen} onMobileClose={handleDrawerToggle} />}
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              p: 0,
              width: '100%',
                minWidth: 0,
                maxWidth: '100%',
                boxSizing: 'border-box',
                overflowX: 'hidden',
              ml: 0,
              mt: isAccountantRoute
                ? 0
                : notificationVisible
                  ? { xs: '92px', sm: '92px' }
                  : { xs: '64px', sm: '70px' },
              minHeight: isAccountantRoute
                ? '100vh'
                : notificationVisible
                  ? { xs: 'calc(100vh - 92px)', sm: 'calc(100vh - 92px)' }
                  : { xs: 'calc(100vh - 64px)', sm: 'calc(100vh - 70px)' },
              transition: 'margin-top 0.3s ease-in-out',
            }}
          >
            <Routes>
              {/* ✅ Dashboard */}
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Dashboard" requiredPermission="view">
                      <DashboardPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />

              {/* ✅ Updated Customer Routes */}
              <Route
                path="/customers"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Customers" requiredPermission="view">
                      <CustomersPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/customers/add"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Customers" requiredPermission="create">
                      <AddCustomerPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/customers/edit/:id"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Customers" requiredPermission="edit">
                      <AddCustomerPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/customers/detail/:id"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Customers" requiredPermission="view">
                      <CustomerDetailPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/customer/:id"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Customers" requiredPermission="view">
                      <CustomerDetailPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />

              {/* ✅ Product Routes */}
              <Route
                path="/products"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Products" requiredPermission="view">
                      <ProductsListPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/products/add"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Products" requiredPermission="create">
                      <AddProductPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/products/edit/:id"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Products" requiredPermission="edit">
                      <AddProductPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/products/detail/:id"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Products" requiredPermission="view">
                      <ProductDetailPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />

              {/* ✅ Invoice Routes */}
              <Route
                path="/invoices"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Invoices" requiredPermission="view">
                      <ListInvoices />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/invoices/add"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Invoices" requiredPermission="create">
                      <AddInvoicePage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/invoices/edit/:id"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Invoices" requiredPermission="edit">
                      <AddInvoicePage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/invoices/view/:id"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Invoices" requiredPermission="view">
                      <InvoiceViewPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/sales/received-peppol-invoices"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="ReceivedInvoices" requiredPermission="view">
                      <ReceivedPeppolInvoices />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/sales/received-peppol-invoices/:uniqueId"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="ReceivedInvoices" requiredPermission="view">
                      <PeppolInvoiceView />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />

              {/* ✅ Supplier Routes */}
              <Route
                path="/suppliers"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Suppliers" requiredPermission="view">
                      <ListSuppliers />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route path="/profile-settings" element={<PrivateRoute><ModuleProtectedRoute moduleName="User Settings" requiredPermission="view"><ProfileSettingsPage /></ModuleProtectedRoute></PrivateRoute>} />
              <Route
                path="/suppliers/add"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Suppliers" requiredPermission="create">
                      <AddSupplier />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/suppliers/edit/:id"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Suppliers" requiredPermission="edit">
                      <EditSupplier />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/suppliers/view/:id"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Suppliers" requiredPermission="view">
                      <SupplierDetailPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />

              {/* ✅ Credit Notes Routes */}
              <Route
                path="/sales/credit-notes"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="CreditNotes" requiredPermission="view">
                      <ListCreditNotesPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/sales/credit-notes/add"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="CreditNotes" requiredPermission="create">
                      <AddCreditNotePage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/sales/credit-notes/:id"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="CreditNotes" requiredPermission="view">
                      <CreditNoteDetailPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />

              {/* ✅ Delivery Notes Routes */}
              <Route
                path="/sales/delivery-notes"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Delivery Notes" requiredPermission="view">
                      <ListDeliveryNotes />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/sales/delivery-notes/add"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Delivery Notes" requiredPermission="create">
                      <AddDeliveryNotePage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/sales/delivery-notes/edit/:id"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Delivery Notes" requiredPermission="edit">
                      <AddDeliveryNotePage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/sales/delivery-notes/view/:id"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Delivery Notes" requiredPermission="view">
                      <DeliveryNoteViewPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />

              {/* ✅ Offers / Proposals Routes */}
              <Route
                path="/sales/offers"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Offers" requiredPermission="view">
                      <ListOffersPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/sales/offers/add"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Offers" requiredPermission="create">
                      <AddOfferPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/sales/offers/edit/:id"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Offers" requiredPermission="edit">
                      <AddOfferPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/sales/offers/:id"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Offers" requiredPermission="view">
                      <OfferDetailPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />

              {/* ✅ Sale Orders Routes */}
              <Route
                path="/sales/sale-orders"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Sale Orders" requiredPermission="view">
                      <ListSaleOrderPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/sales/sale-orders/add"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Sale Orders" requiredPermission="create">
                      <AddSaleOrderPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/sales/sale-orders/convert"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Sale Orders" requiredPermission="edit">
                      <ConvertToInvoicePage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/sales/sale-orders/:id"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Sale Orders" requiredPermission="view">
                      <SaleOrderDetailPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />

              {/* ✅ Other Routes */}
              <Route
                path="/purchases"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Purchases" requiredPermission="view">
                      <PurchaseOrdersListPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/reports"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Reports" requiredPermission="view">
                      <ReportsPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/reports/cash-ledger"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Reports" requiredPermission="view">
                      <CashMovementLedgerPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/payments"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Payments" requiredPermission="view">
                      <PaymentPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/company-settings"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Company Settings" requiredPermission="view">
                      <CompanySettingsPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/store-user-settings"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Store & User Management" requiredPermission="view">
                      <AddStoreAndUser />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/announcements"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Announcements" requiredPermission="view">
                      <AnnouncementPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/wishlist"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Wishlist" requiredPermission="view">
                      <WishlistPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/purchases/add"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Purchases" requiredPermission="create">
                      <AddPurchaseOrderPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/purchases/:id"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Purchases" requiredPermission="view">
                      <PurchaseOrderDetailPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/purchases/edit/:id"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Purchases" requiredPermission="update">
                      <EditPurchaseOrderPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
            
              <Route
                path="/shopify/products"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Shopify" requiredPermission="view">
                      <ShopifyProductsListPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/shopify/products/add"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Shopify" requiredPermission="create">
                      <AddShopifyProduct />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/shopify/products/view/:productId"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Shopify" requiredPermission="view">
                      <ShopifyProductDetailPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/shopify/products/edit/:productId"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Shopify" requiredPermission="edit">
                      <EditShopifyProductPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/shopify/customers"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Shopify" requiredPermission="view">
                      <ShopifyCustomersPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/shopify/orders"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Shopify" requiredPermission="view">
                      <ShopifyOrdersPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              {/* POS route handled separately with its own layout */}
              <Route
                path="/tickets"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="POS" requiredPermission="view">
                      <TicketDeCaissePage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/tickets/view/:id"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="POS" requiredPermission="view">
                      <TicketDetailPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/tickets/edit/:id"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="POS" requiredPermission="create">
                      <TicketEditPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />

              {/* ✅ Inventory Route */}
              <Route
                path="/inventory"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Inventory" requiredPermission="view">
                      <InventoryPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/inventory/:productId"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Inventory" requiredPermission="view">
                      <ProductInventoryDetail />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />

              {/* ✅ Inventory Item Tracking (IMEI/Serial) */}
              <Route
                path="/inventory/items"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Inventory" requiredPermission="view">
                      <InventoryItemsPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />

              {/* ✅ Inventory Transfers */}
              <Route
                path="/inventory/transfers"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Inventory" requiredPermission="view">
                      <TransferListPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/inventory/transfers/create"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Inventory" requiredPermission="create">
                      <CreateTransferPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />

              {/* ✅ Product Variants */}
              <Route
                path="/products/variants"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Products" requiredPermission="view">
                      <ProductVariantsPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />

              {/* ✅ Access Management Route - CompanyAdmin Only */}
              <Route
                path="/access-management"
                element={
                  <PrivateRoute>
                    <AccessManagementPage />
                  </PrivateRoute>
                }
              />

              {/* ✅ Admin User Management Routes */}
              <Route
                path="/admin-user-management"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Admin User Management" requiredPermission="view">
                      <AdminUserManagementPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin-user-management/company/:companyId"
                element={
                  <PrivateRoute>
                    <ModuleProtectedRoute moduleName="Admin User Management" requiredPermission="view">
                      <CompanyEditPage />
                    </ModuleProtectedRoute>
                  </PrivateRoute>
                }
              />

              {/* ✅ Accountant Portal Routes */}
              <Route
                path="/accountant-portal/dashboard"
                element={
                  <PrivateRoute>
                    <AccountantDashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/accountant-portal/clients"
                element={
                  <PrivateRoute>
                    <ClientsListPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/accountant-portal/clients/:id"
                element={
                  <PrivateRoute>
                    <ClientDetailPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/accountant-portal/clients/create"
                element={
                  <PrivateRoute>
                    <CreateClientPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/accountant-portal/clients/:id/edit"
                element={
                  <PrivateRoute>
                    <EditClientPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/accountant-portal/invoices/incoming"
                element={
                  <PrivateRoute>
                    <IncomingInvoicesPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/accountant-portal/invoices/outgoing"
                element={
                  <PrivateRoute>
                    <OutgoingInvoicesPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/accountant-portal/invoices/credit-notes"
                element={
                  <PrivateRoute>
                    <CreditNotesPageAP />
                  </PrivateRoute>
                }
              />
              <Route
                path="/accountant-portal/banking"
                element={
                  <PrivateRoute>
                    <BankingPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/accountant-portal/documents"
                element={
                  <PrivateRoute>
                    <DocumentsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/accountant-portal/vat-taxes"
                element={
                  <PrivateRoute>
                    <VATTaxesPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/accountant-portal/reports"
                element={
                  <PrivateRoute>
                    <ReportsPageAP />
                  </PrivateRoute>
                }
              />
              <Route
                path="/accountant-portal/tasks"
                element={
                  <PrivateRoute>
                    <TasksPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/accountant-portal/tasks/create"
                element={
                  <PrivateRoute>
                    <CreateTaskPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/accountant-portal/tasks/:id/edit"
                element={
                  <PrivateRoute>
                    <CreateTaskPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/accountant-portal/messages"
                element={
                  <PrivateRoute>
                    <MessagesPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/accountant-portal/calendar"
                element={
                  <PrivateRoute>
                    <CalendarPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/accountant-portal/settings"
                element={
                  <PrivateRoute>
                    <SettingsPageAP />
                  </PrivateRoute>
                }
              />

              {/* ✅ Unauthorized Page */}
              <Route
                path="/unauthorized"
                element={
                  <PrivateRoute>
                    <UnauthorizedPage />
                  </PrivateRoute>
                }
              />

              {/* ✅ 404 Catch-All Route */}
              <Route
                path="*"
                element={
                  <PrivateRoute>
                    <NotFoundPage />
                  </PrivateRoute>
                }
              />
            </Routes>
          </Box>
        </Box>
      ) : (
        <>
          {/* ✅ OfflineNotificationBar for auth pages */}
          {['/login', '/register', '/forgot-password'].includes(location.pathname) && <OfflineNotificationBar />}
          
          <Routes>
            {/* ✅ Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/home" element={<HomePage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            
            {/* ✅ Auth Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </>
      )}
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AuthInitializer />
        <div
          style={{
            minHeight: '100vh',
            width: '100%',
            background: '#fafcff',
            backgroundImage: 'url("https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1200&q=80")',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(250,252,255,0.9)',
              zIndex: 0,
            }}
          />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <AppContent />
            <GlobalLoader />
          </div>
          <ToastContainer 
            position="bottom-center"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
            style={{ zIndex: 9999 }}
          />
        </div>
      </Router>
    </AuthProvider>
  );
};

export default App;